import { useMemo } from 'react';
import { create } from 'zustand';
import { combine, subscribeWithSelector } from 'zustand/middleware';
import { type Result } from '../../src/types';
import { eventBus } from '../event-manager';
import {
  addScreenShareParticipant,
  initDeviceList,
  removeScreenShareParticipant,
  updateAudioInputDeviceState,
  updateVideoInputDeviceState,
} from '../handlers';
import { createLocalTrack } from '../handlers/track';
import { type MediaType } from '../types';
import {
  differenceBy,
  EVENT_LISTENER_METHODS,
  getMediaType,
  INTERNAL_EVENTS,
  isArrayOfObjEqual,
  MEDIA_TYPE,
  tryCatch,
  VIDEO_PAUSE_AUTHORITY,
  waitForStoreState,
} from '../utils';
import { sendParticipantEvent } from '../utils/send-participant-event';
import { useBaseStore } from './base';
import { useConferenceStore } from './conference';
import { useParticipantStore } from './participants';

type Track = {
  // isReceivingData: boolean;
  local: boolean;
  mediaType: MediaType;
  // mirror: boolean;
  muted: number;
  pid: string;
  videoType: 'desktop' | 'camera' | null;
  originalTrack?: any;
};

type TracksState = {
  tracks: Track[];
  permissions: {
    audio: boolean;
    video: boolean;
    initialized: boolean;
  };
};

type Actions = {
  reset: () => void;
  addTrack: (originalTrack: any) => void;
  removeTrack: (originalTrack: any) => void;
  updateTrack: (originalTrack: any, updatedTrack: Partial<Track>) => void;
  updateLocalTrack: (
    mediaType: MediaType,
    updatedTrack: Partial<Track>,
  ) => void;
};

const initialState: TracksState = {
  tracks: [],
  permissions: {
    audio: false,
    video: false,
    initialized: false,
  },
};

export const useTracksStore = create<TracksState & Actions>()(
  subscribeWithSelector(
    combine(initialState, (set, get) => ({
      reset: () =>
        set({
          ...initialState,
          // Preserve permissions across resets - they're queried once at SDK init
          // and shouldn't be lost when joining/leaving sessions
          permissions: get().permissions,
        }),
      addTrack: (originalTrack: any) =>
        set(state => {
          const isLocal = originalTrack.isLocal();
          const participantId = (() => {
            if (originalTrack.getParticipantId()) {
              return originalTrack.getParticipantId();
            }
            if (isLocal) {
              return useParticipantStore.getState().localParticipant?.pid;
            }
            return originalTrack.getId();
          })();
          const track: Track = {
            local: isLocal,
            mediaType: getMediaType(originalTrack),
            pid: participantId,
            videoType: originalTrack.getVideoType(),
            muted: originalTrack.isMuted() ? 1 : 0,
            originalTrack,
          };
          return {
            tracks: [...state.tracks, track],
          };
        }),
      removeTrack: (originalTrack: any) =>
        set(state => ({
          tracks: state.tracks.filter(
            track => track.originalTrack !== originalTrack,
          ),
        })),
      updateTrack: (originalTrack: any, updatedTrack: Partial<Track>) =>
        set(state => {
          return {
            tracks: state.tracks.map(track =>
              track.originalTrack === originalTrack
                ? {
                    ...track,
                    mediaType: track.mediaType ?? getMediaType(originalTrack),
                    videoType: track.videoType ?? originalTrack.getVideoType(),
                    ...updatedTrack,
                  }
                : track,
            ),
          };
        }),
      updateLocalTrack: (mediaType: MediaType, updatedTrack: Partial<Track>) =>
        set(state => {
          const localTrack = state.tracks.find(
            track => track.local && track.mediaType === mediaType,
          );
          if (!localTrack) {
            const localParticipant =
              useParticipantStore.getState().localParticipant;
            return {
              tracks: [
                ...state.tracks,
                {
                  local: true,
                  mediaType,
                  pid: localParticipant.pid,
                  videoType:
                    mediaType === MEDIA_TYPE.SCREENSHARE
                      ? 'desktop'
                      : mediaType === MEDIA_TYPE.VIDEO
                        ? 'camera'
                        : null,
                  muted: updatedTrack.muted ?? 0,
                  ...updatedTrack,
                },
              ],
            };
          }

          return {
            tracks: state.tracks.map(track =>
              track === localTrack
                ? {
                    ...track,
                    ...updatedTrack,
                    mediaType: mediaType,
                    videoType:
                      mediaType === MEDIA_TYPE.SCREENSHARE
                        ? 'desktop'
                        : mediaType === MEDIA_TYPE.VIDEO
                          ? 'camera'
                          : null,
                  }
                : track,
            ),
          };
        }),
    })),
  ),
);

export const useTrackByParticipantId = (
  participantId: string,
  mediaType: MediaType = MEDIA_TYPE.VIDEO,
) => {
  const tracks = useTracksStore(state => state.tracks);

  const track = useMemo(
    () =>
      tracks.find(t => t.pid === participantId && t.mediaType === mediaType),
    [tracks, participantId, mediaType],
  );
  return track;
};

export const getTrackByParticipantId = (
  participantId: string,
  mediaType: MediaType = MEDIA_TYPE.VIDEO,
) => {
  const tracks = useTracksStore.getState().tracks;

  return tracks.find(t => t.pid === participantId && t.mediaType === mediaType);
};

export const useLocalTrack = (mediaType: MediaType = MEDIA_TYPE.VIDEO) => {
  const tracks = useTracksStore(state => state.tracks);
  return useMemo(
    () => tracks.find(t => t.local && t.mediaType === mediaType),
    [tracks, mediaType],
  );
};

export const getLocalTrack = (mediaType: MediaType = MEDIA_TYPE.VIDEO) => {
  const tracks = useTracksStore.getState().tracks;
  return tracks.find(t => t.local && t.mediaType === mediaType);
};

export const disposeLocalTracks = async () => {
  const allLocalTracks = useTracksStore
    .getState()
    .tracks.filter(track => track.local);
  const disposePromises: Promise<Result<unknown, Error>>[] = [];
  for (const { originalTrack } of allLocalTracks) {
    if (originalTrack && originalTrack.disposed !== true) {
      disposePromises.push(tryCatch(originalTrack.dispose()));
    }
  }
  const result = await Promise.all(disposePromises);
  for (const { error } of result) {
    if (error) {
      console.error('Error disposing track:', error);
    }
  }
};

export const muteAudioTrack = () => {
  useTracksStore.getState().updateLocalTrack(MEDIA_TYPE.AUDIO, {
    muted: 1,
  });
};

export const unMuteAudioTrack = async () => {
  let localAudioTrack = getLocalTrack(MEDIA_TYPE.AUDIO);
  const permissions = useTracksStore.getState().permissions;
  const isMobileSDK = useBaseStore.getState().isMobileSDK();
  if (!permissions.audio && permissions.initialized && !isMobileSDK) {
    eventBus.publish({
      type: INTERNAL_EVENTS.notification,
      payload: {
        type: 'error',
        message:
          'Permission denied for audio track. Please allow access to your device.',
      },
    });
    eventBus.publish({
      type: INTERNAL_EVENTS.queryPermission,
    });
    return;
  }
  if (!localAudioTrack?.originalTrack) {
    await createLocalTrack('audio');
    localAudioTrack = getLocalTrack(MEDIA_TYPE.AUDIO);
  }
  if (!localAudioTrack?.originalTrack) {
    return;
  }
  useTracksStore.getState().updateLocalTrack(MEDIA_TYPE.AUDIO, {
    muted: 0,
  });
};

export const toggleAudioTrack = () => {
  const muted = getLocalTrack(MEDIA_TYPE.AUDIO)?.muted;
  if (muted) {
    unMuteAudioTrack();
  } else {
    muteAudioTrack();
  }
};

export const pauseVideoTrack = (authority = VIDEO_PAUSE_AUTHORITY.USER) => {
  const muted = (getLocalTrack(MEDIA_TYPE.VIDEO)?.muted || 0) | authority;
  useTracksStore.getState().updateLocalTrack(MEDIA_TYPE.VIDEO, {
    muted: muted,
  });
};

export const resumeVideoTrack = async (
  authority = VIDEO_PAUSE_AUTHORITY.USER,
) => {
  let localVideoTrack = getLocalTrack(MEDIA_TYPE.VIDEO);
  const permissions = useTracksStore.getState().permissions;
  const isMobileSDK = useBaseStore.getState().isMobileSDK();
  if (!permissions.video && permissions.initialized && !isMobileSDK) {
    eventBus.publish({
      type: INTERNAL_EVENTS.notification,
      payload: {
        type: 'error',
        message:
          'Permission denied for video track. Please allow access to your device.',
      },
    });
    eventBus.publish({
      type: INTERNAL_EVENTS.queryPermission,
    });
    return;
  }
  if (!localVideoTrack?.originalTrack) {
    await createLocalTrack(MEDIA_TYPE.VIDEO);
    localVideoTrack = getLocalTrack(MEDIA_TYPE.VIDEO);
  }
  if (!localVideoTrack?.originalTrack) {
    return;
  }
  const muted = localVideoTrack.muted & ~authority;
  useTracksStore.getState().updateLocalTrack(MEDIA_TYPE.VIDEO, {
    muted: muted,
  });
};

export const toggleVideoTrack = () => {
  const videoMuted = getLocalTrack(MEDIA_TYPE.VIDEO)?.muted;
  if (videoMuted) {
    resumeVideoTrack();
  } else {
    pauseVideoTrack();
  }
};

useTracksStore.subscribe(
  state =>
    state.tracks.find(t => t.mediaType === MEDIA_TYPE.AUDIO && t.local)?.muted,
  (muted, prevMuted) => {
    console.log(
      'Local participant muted state changed audio:',
      muted,
      prevMuted,
    );
    const audioTrack = getLocalTrack(MEDIA_TYPE.AUDIO)?.originalTrack;
    if (muted && audioTrack) {
      audioTrack.mute();
    } else if (audioTrack) {
      audioTrack.unmute();
    }
    if (typeof muted === 'number' && typeof prevMuted === 'number') {
      eventBus.publish({
        type: muted
          ? EVENT_LISTENER_METHODS.MediaEventsListener.onAudioMuted
          : EVENT_LISTENER_METHODS.MediaEventsListener.onAudioUnMuted,
      });
    }
  },
);

useTracksStore.subscribe(
  state =>
    state.tracks.find(t => t.mediaType === MEDIA_TYPE.VIDEO && t.local)?.muted,
  (videoMuted, prevVideoMuted) => {
    console.log('Local participant muted state changed video:', videoMuted);
    const videoTrack = getLocalTrack(MEDIA_TYPE.VIDEO)?.originalTrack;
    if (videoMuted && videoTrack) {
      videoTrack.mute();
    } else if (videoTrack) {
      videoTrack.unmute();
    }
    if (typeof videoMuted === 'number' && typeof prevVideoMuted === 'number') {
      eventBus.publish({
        type: videoMuted
          ? EVENT_LISTENER_METHODS.MediaEventsListener.onVideoPaused
          : EVENT_LISTENER_METHODS.MediaEventsListener.onVideoResumed,
      });
    }
  },
);

useTracksStore.subscribe(
  state =>
    state.tracks.find(t => t.mediaType === MEDIA_TYPE.SCREENSHARE && t.local)
      ?.muted,
  muted => {
    const track = getLocalTrack(MEDIA_TYPE.SCREENSHARE)?.originalTrack;
    if (muted && track) {
      track.mute();
    }
  },
);

useTracksStore.subscribe(
  state =>
    state.tracks.find(t => t.mediaType === MEDIA_TYPE.AUDIO && t.local)
      ?.originalTrack,
  audioTrack => {
    if (audioTrack) {
      const muted = getLocalTrack(MEDIA_TYPE.AUDIO)?.muted;
      if (muted) {
        audioTrack.mute();
      } else {
        audioTrack.unmute();
      }
    }
  },
);

useTracksStore.subscribe(
  state =>
    state.tracks.find(t => t.mediaType === MEDIA_TYPE.VIDEO && t.local)
      ?.originalTrack,
  videoTrack => {
    if (videoTrack) {
      const videoMuted = getLocalTrack(MEDIA_TYPE.VIDEO)?.muted;
      if (videoMuted) {
        videoTrack.mute();
      } else {
        videoTrack.unmute();
      }
    }
  },
);

useTracksStore.subscribe(
  state =>
    state.tracks.find(t => t.mediaType === MEDIA_TYPE.AUDIO && t.local)
      ?.originalTrack,
  (track, prevTrack) => {
    const conference = useConferenceStore.getState().conference;
    if (conference) {
      if (prevTrack && track) {
        conference.replaceTrack(prevTrack, track);
      } else if (track) {
        conference.addTrack(track);
      }
    }
    if (track) {
      const deviceId = track.getDeviceId();
      waitForStoreState(
        useBaseStore,
        state => state.audioInputDevices.length > 0,
      )
        .then(() => {
          const audioInputDevices = useBaseStore.getState().audioInputDevices;
          const device = audioInputDevices.find(d => d.deviceId === deviceId);
          updateAudioInputDeviceState(device, true);
        })
        .catch(() => {
          // Timed out waiting for devices — update with undefined
          updateAudioInputDeviceState(undefined, true);
        });
    }
  },
);

useTracksStore.subscribe(
  state =>
    state.tracks.find(t => t.mediaType === MEDIA_TYPE.VIDEO && t.local)
      ?.originalTrack,
  (track, prevTrack) => {
    const conference = useConferenceStore.getState().conference;
    if (conference) {
      if (prevTrack && track) {
        conference.replaceTrack(prevTrack, track);
      } else if (track) {
        conference.addTrack(track);
      }
    }
    if (track) {
      const deviceId = track.getDeviceId();
      const device = useBaseStore
        .getState()
        .videoInputDevices.find(d => d.deviceId === deviceId);
      updateVideoInputDeviceState(device, true);
    }
  },
);

useTracksStore.subscribe(
  state =>
    state.tracks.find(t => t.mediaType === MEDIA_TYPE.SCREENSHARE && t.local)
      ?.originalTrack,
  (track, prevTrack) => {
    const conference = useConferenceStore.getState().conference;
    if (conference) {
      if (prevTrack && track) {
        conference.replaceTrack(prevTrack, track).then(() => {
          // Clean up event listeners and dispose the old track after replacement
          prevTrack.removeAllListeners();
          prevTrack.dispose();
        });
      } else if (track) {
        conference.addTrack(track);
      }
    }
  },
);

useTracksStore.subscribe(
  state =>
    state.tracks.filter(t => t.mediaType === MEDIA_TYPE.SCREENSHARE && !t.muted)
      .length,
  () => {
    const screenShareTracks = useTracksStore
      .getState()
      .tracks.filter(t => t.mediaType === MEDIA_TYPE.SCREENSHARE && !t.muted);
    const vitualParticipants = useParticipantStore
      .getState()
      .virtualParticipants.filter(p => p.type === 'screen-share');

    const tracksToAdd = differenceBy(
      screenShareTracks,
      vitualParticipants,
      x => x.pid,
    );
    const tracksToRemove = differenceBy(
      vitualParticipants,
      screenShareTracks,
      x => x.pid,
    );
    tracksToRemove.forEach(track => {
      removeScreenShareParticipant(track.pid);
    });
    tracksToAdd.forEach(t => addScreenShareParticipant(t.originalTrack));
  },
);

useTracksStore.subscribe(
  state => state.permissions,
  (permissions, prevPermissions) => {
    const conferenceStatus = useConferenceStore.getState().conferenceStatus;
    if (conferenceStatus !== 'joined' && conferenceStatus !== 'restored') {
      return;
    }
    if (permissions.audio === false && prevPermissions.audio === true) {
      eventBus.publish({
        type: INTERNAL_EVENTS.notification,
        payload: {
          type: 'error',
          message:
            'Permission denied for audio track. Please allow access to your device.',
        },
      });
      muteAudioTrack();
    }
    if (permissions.video === false && prevPermissions.video === true) {
      eventBus.publish({
        type: INTERNAL_EVENTS.notification,
        payload: {
          type: 'error',
          message:
            'Permission denied for video track. Please allow access to your device.',
        },
      });
      pauseVideoTrack();
    }
  },
);

useTracksStore.subscribe(
  state => state.permissions.audio || state.permissions.video,
  anyPermissionGranted => {
    if (anyPermissionGranted) {
      initDeviceList();
    }
  },
);

useTracksStore.subscribe(
  state =>
    state.tracks
      .filter(t => t.mediaType === MEDIA_TYPE.AUDIO && !t.local)
      .map(t => ({
        muted: t.muted,
        pid: t.pid,
      })),
  (tracks, prevTracks) => {
    prevTracks.forEach(prevTrack => {
      const newTrack = tracks.find(t => t.pid === prevTrack.pid);
      if (newTrack && newTrack.muted !== prevTrack.muted) {
        sendParticipantEvent(
          newTrack.muted
            ? EVENT_LISTENER_METHODS.ParticipantEventsListner
                .onParticipantAudioMuted
            : EVENT_LISTENER_METHODS.ParticipantEventsListner
                .onParticipantAudioUnmuted,
          newTrack.pid,
        );
      }
    });
  },
  {
    equalityFn: isArrayOfObjEqual,
  },
);

useTracksStore.subscribe(
  state =>
    state.tracks
      .filter(t => t.mediaType === MEDIA_TYPE.VIDEO && !t.local)
      .map(t => ({
        muted: t.muted,
        pid: t.pid,
      })),
  (tracks, prevTracks) => {
    prevTracks.forEach(prevTrack => {
      const newTrack = tracks.find(t => t.pid === prevTrack.pid);
      if (newTrack && newTrack.muted !== prevTrack.muted) {
        sendParticipantEvent(
          newTrack.muted
            ? EVENT_LISTENER_METHODS.ParticipantEventsListner
                .onParticipantVideoPaused
            : EVENT_LISTENER_METHODS.ParticipantEventsListner
                .onParticipantVideoResumed,
          newTrack.pid,
        );
      }
    });
  },
  {
    equalityFn: isArrayOfObjEqual,
  },
);
